// /en/notary/documents — Wave 60: EN mirror of the six Notary document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { NOTARY_DOC_GROUPS_EN } from "@/data/notary-doc-groups-en";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/en/notary/documents`;
const TITLE = "Documents a Thai Notary Public Can Certify — All 6 Families | NYC";
const DESC =
  "Every document a registered Thai Notarial Services Attorney can certify, grouped into six families, with preparation checklists, the current legalisation chain and the limits you should know before filing.";

export const Route = createFileRoute("/en/notary/documents/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/notary/documents", "en") },
      { name: "description", content: ctaDesc(DESC, "/en/notary/documents", "en") },
      { property: "og:title", content: ctaTitle(TITLE, "/en/notary/documents", "en") },
      { property: "og:description", content: ctaDesc(DESC, "/en/notary/documents", "en") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: `${SITE}/notary/documents` },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE}/notary/documents` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "en",
          mainEntity: NOTARY_DOC_GROUPS_EN.flatMap((g) =>
            g.faqsEn.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "Document families certified by a Thai Notarial Services Attorney",
          numberOfItems: NOTARY_DOC_GROUPS_EN.length,
          itemListElement: NOTARY_DOC_GROUPS_EN.map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.titleEn,
            url: `${URL}#${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: NotaryDocumentsEnPage,
});

function NotaryDocumentsEnPage() {
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/en/notary" className="hover:underline">
          Notary Public
        </Link>{" "}
        / Documents we certify
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        Documents a Thai Notary Public can certify — all six families
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        A Notarial Services Attorney registered with the Lawyers Council of Thailand performs three core
        acts: certifying that a copy matches the original inspected, witnessing a signature executed in
        person, and administering an oath on a written declaration. This page groups every document type by
        how it is used, with preparation checklists and the limits to know before filing.
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>Thailand's Apostille status:</strong> Thailand deposited its instrument of accession on{" "}
        {APOSTILLE_STATUS.accessionDepositEn}, and the Convention enters into force for Thailand on{" "}
        {APOSTILLE_STATUS.entryIntoForceEn}. Until then, documents still pass through the Department of
        Consular Affairs and the destination embassy.{" "}
        <a
          href={APOSTILLE_STATUS.sourceUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline"
        >
          Source: HCCH
        </a>
      </aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {NOTARY_DOC_GROUPS_EN.map((g) => (
          <li key={g.slug}>
            <Link
              to="/en/notary/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleEn}
            </Link>
          </li>
        ))}
      </ul>


      {NOTARY_DOC_GROUPS_EN.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-14 scroll-mt-24">
          <h2 className="text-2xl font-semibold tracking-tight">{g.titleEn}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryEn}</p>

          <div className="mt-4 space-y-4 text-base leading-8">
            {g.bodyEn.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <h3 className="mt-6 text-lg font-semibold">Documents in this family</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
            {g.documentsEn.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>

          <h3 className="mt-6 text-lg font-semibold">Acts performed</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
            {g.actsEn.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>

          <h3 className="mt-6 text-lg font-semibold">Preparation checklist</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
            {g.checklistEn.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>

          <h3 className="mt-6 text-lg font-semibold">FAQs</h3>
          <div className="mt-2 space-y-3">
            {g.faqsEn.map((f) => (
              <div key={f.q} className="rounded-lg border border-border p-4">
                <p className="font-medium">{f.q}</p>
                <p className="mt-1 text-sm leading-7 text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      ))}
          <ServiceAuthorityBlock topic="notary" locale="en" />
    </main>
  );
}
